"use client";

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";

interface OverlayDialogProps {
  isOpen: boolean;
  hasCloseBtn?: boolean;
  onClose?: () => void;
  title?: ReactNode;
  children: ReactNode;
  size?: "small" | "medium" | "large";
}

const SIZE_CLASS: Record<NonNullable<OverlayDialogProps["size"]>, string> = {
  small: "max-w-[300px]",
  medium: "max-w-[500px]",
  large: "max-w-[800px]",
};

export const OverlayDialog = ({
  isOpen,
  hasCloseBtn = true,
  onClose,
  title = "",
  children,
  size = "medium",
}: OverlayDialogProps) => {
  const modalRef = useRef<HTMLDialogElement | null>(null);

  const sizeClass = SIZE_CLASS[size];

  useEffect(() => {
    const modalElement = modalRef.current;
    if (modalElement) {
      if (isOpen) {
        modalElement.showModal();
        modalElement.focus();
      } else {
        modalElement.close();
      }
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    onClose?.();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") {
      handleCloseModal();
    }
  };

  return (
    <dialog
      ref={modalRef}
      data-testid="modal-dialog"
      className={`overlay-dialog-component fixed top-[15%] left-1/2 -translate-x-1/2 z-50 w-full ${sizeClass} max-h-[500px] rounded-md shadow-md p-0 border-none outline-none`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseModal();
        }
        e.stopPropagation();
      }}
    >
      <div>
        <div
          className={`p-8 relative bg-white transition-all duration-300 ease-in-out ${
            isOpen
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-4"
          }`}
        >
          {hasCloseBtn && (
            <button
              data-testid="close-button"
              className="absolute top-4 right-4 text-black hover:text-gray-600 cursor-pointer"
              onClick={handleCloseModal}
              aria-label="close dialog"
              tabIndex={0}
            >
              <Icon icon={ICONS.DISMISS} />
            </button>
          )}

          {title && (
            <h2
              id="modal-title"
              className="text-xl font-semibold mb-4"
              data-testid="modal-title"
            >
              {title}
            </h2>
          )}

          <div className="mb-4" data-testid="modal-content">
            {children}
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default OverlayDialog;
